import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FixedFill } from '@peacecover/icon';
import { useGetFanLettersInfinite } from '../../domains/bettie/ui/hooks/useGetFanLettersInfinite';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { attachJosa } from '../../utils/bettieUtils';
import { dateTimeDistanceToNow } from '../../utils/DateUtil';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import type { components } from '../../types/schema';

type FanLetter = components['schemas']['ApiV1FanLetter'];

const BettieLetterListPage = () => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetFanLettersInfinite({
    only_unseen: false,
    per_page: 10
  });

  const fanLetters = data?.pages.flatMap((page) => page.fanLetters ?? []) ?? [];
  const hasFanLetters = fanLetters && fanLetters.length > 0;

  const handleEndReached = () => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-light dark:bg-background-dark">
      <Helmet>
        <title>{t('BettieLetterList.title')}</title>
      </Helmet>
      <header className="fixed top-0 left-0 right-0 z-10 px-[16px] pt-[calc(12px+env(safe-area-inset-top))] pb-[12px] bg-background-light dark:bg-background-dark">
        <div className="relative flex items-center justify-center w-full">
          <button
            type="button"
            className="absolute left-0 top-1/2 -translate-y-1/2"
            onClick={handleHistoryBack}
          >
            <ArrowLeft size={24} className="text-text-main-lv1" />
          </button>
          <h1 className="text-text-main-lv1 text-[17px] font-bold leading-[20px]">
            {t('BettieLetterList.title')}
          </h1>
        </div>
      </header>
      {hasFanLetters && (
        <div className="flex items-center gap-[8px] px-[12px] py-[14px] mx-[8px] mt-[50px] bg-pure-white dark:bg-pure-black rounded-[12px] shadow-[0_0_4px_0_rgba(0,0,0,0.05),_0_0_11.6px_0_rgba(0,0,0,0.05)]">
          <div className="p-[3px] rounded-full bg-shadow dark:bg-surface-lv2">
            <FixedFill size={13} className="text-text-main-lv3" />
          </div>
          <p className="text-text-main-lv3 text-[13px] font-medium leading-[16px]">
            {t('BettieLetterList.description')}
          </p>
        </div>
      )}
      <div className="px-[16px] mt-[10px]">
        {hasFanLetters ? (
          <Virtuoso
            useWindowScroll
            className="w-full"
            data={fanLetters}
            endReached={handleEndReached}
            computeItemKey={(_, fanLetter) => fanLetter.fanLetterId}
            itemContent={(_, fanLetter) => <LetterItem fanLetter={fanLetter} />}
          />
        ) : (
          <EmptyCase />
        )}
      </div>
    </div>
  );
};

export default BettieLetterListPage;

const LetterItem = ({ fanLetter }: { fanLetter: FanLetter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const truncateUserNickname = (nickname: string) =>
    nickname.length > 3 ? `${nickname.slice(0, 3)}..` : nickname;
  return (
    <button
      type="button"
      onClick={() => navigate(`/bettie/letter/${fanLetter.fanLetterId}`)}
      className="py-[16px] flex items-center gap-[12px] border-b border-separator1 dark:border-separator2 w-full"
    >
      <ProfileAvatar
        widthAndHeight="34px"
        svgWidthAndHeight="34px"
        thumbnailUrl={fanLetter.bettiePictureUrl}
      />
      <div className="flex items-end gap-[6px]">
        <p className="text-[14px] font-medium text-text-main-lv2 leading-[17px]">
          {t('BettieLetterList.letterSent', {
            artist: attachJosa(fanLetter.bettieNickname ?? '', '가/이가'),
            nickname: truncateUserNickname(fanLetter.userNickname ?? '')
          })}
        </p>
        <span className="text-[13px] font-medium text-text-main-lv4 leading-[16px]">
          {fanLetter.writtenAt
            ? dateTimeDistanceToNow(fanLetter.writtenAt)
            : t('BettieLetterList.noRead')}
        </span>
      </div>
    </button>
  );
};

const EmptyCase = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-[12px] mt-[130px]">
      <img
        src="/assets/bettie/bettie_letter.png"
        alt="empty letter"
        className="w-[82px] h-[66px] grayscale"
      />
      <div className="px-[24px] py-[10px] flex flex-col gap-[14px] text-center">
        <p className="text-[17px] font-bold text-text-main-lv2 leading-[20px]">
          {t('BettieLetterList.emptyTitle')}
        </p>
        <p className="text-[14px] font-medium text-text-main-lv4 leading-[17px] whitespace-pre-line">
          {t('BettieLetterList.emptyDescription')}
        </p>
      </div>
    </div>
  );
};
